import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { ReorderRuleContext } from '../../../../context/ReorderRuleContext';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { CompanyRole } from '../../../../types/company_role_assignment/companyRoleAssignment';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { ReorderRule } from '../../../../types/reorderRule';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { StockLocationAggregateType } from '../../../../types/common/stockLocationAggregateType';
import { CompanyContext } from '../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import DeleteReorderRuleModal from './Modals/DeleteReorderRuleModal';
import { StockLocation } from '../../../../types/stock_location/stockLocation';
import { useNavigate } from 'react-router-dom';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ReorderRuleFilter } from '../../../../VentoryUI/components/filters/ReorderRuleFilter';
import Table, { TableHeader } from '../../../components/Table';
import { AddButton, DeleteButton, ExportIconButton } from '../../../components/Button';
import { Routing } from '../../../../util/routes.util';
import SettingsIconButton from '../../../icons/templates/SettingsIconButton';
import { ExportReorderRuleModal } from './Modals/ExportReorderRuleModal';

interface ReorderRulesOverviewPaneProps {
  productMasterDataId?: string;
}

export interface ReorderRuleGroup {
  groupId: string;
  rules: ReorderRule[];
}

const getHeaders = (
  productMasterData: Map<string, ProductMasterData>,
  stockLocations: Map<string, StockLocation>,
): TableHeader<ReorderRuleGroup>[] => [
  {
    key: 'name',
    name: t(TranslationKey.name).singular.label,
    text: (item: ReorderRuleGroup) => {
      return item.rules[0].name;
    },
  },
  {
    key: 'products',
    name: t(TranslationKey.product).plural.label,
    text: (item: ReorderRuleGroup) => {
      if (!item?.rules[0]?.productMasterDataId) return 'Any product';
      return new Set(item.rules.map(rule => rule.productMasterDataId)).size.toString();
    },
  },
  {
    key: 'stockLocation',
    name: t(TranslationKey.location).singular.label,
    text: (item: ReorderRuleGroup) => {
      const rule = item?.rules[0];
      const uniqueStockLocations = new Set(item.rules.map(rule => rule.stockLocationId));

      switch (rule?.stockLocationAggregateType) {
        case StockLocationAggregateType.all:
          return 'Across all locations';
        case StockLocationAggregateType.allOf:
          if (uniqueStockLocations.size > 1) {
            return 'Across ' + uniqueStockLocations.size + ' locations';
          } else {
            return `Across ${[...uniqueStockLocations]
              .map(id => stockLocations.get(id || '')?.name || 'Unknown Stock Location')
              .join(' and ')}`;
          }
        case StockLocationAggregateType.any:
          return 'In any location';
        case StockLocationAggregateType.anyOf:
          if (uniqueStockLocations.size > 1) {
            return 'In any of ' + uniqueStockLocations.size + ' locations';
          } else {
            return `In ${[...uniqueStockLocations]
              .map(id => stockLocations.get(id || '')?.name || 'Unknown Stock Location')
              .join(' or ')}`;
          }
      }
    },
  },
];

export default function ReorderRulesOverviewPane({ productMasterDataId }: ReorderRulesOverviewPaneProps) {
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { reorderRulesLoading, groupedReorderRules } = useContext(ReorderRuleContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { stockLocations } = useContext(StockLocationContext);

  const [deletedModalOpen, setDeleteModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filteredRules = useMemo(() => {
    const groups: ReorderRuleGroup[] = [];

    for (const [groupId, rules] of groupedReorderRules) {
      if (productMasterDataId && !rules.some(rule => rule.productMasterDataId === productMasterDataId)) continue;

      if (hasCompanyRole(currentCompany.id, CompanyRole.administrator)) {
        groups.push({ groupId, rules });
        continue;
      } else {
        if (
          rules.some(rule => {
            if (!rule.stockLocationId) return false;
            else
              return !hasStockLocationRole(
                rule.companyId,
                rule.stockLocationId,
                StockLocationRole.STOCK_LOCATION_MANAGER,
              );
          })
        ) {
          continue;
        }
      }

      groups.push({ groupId, rules });
    }

    return groups;
  }, [groupedReorderRules]);

  const [items, setItems] = useState(filteredRules);

  const filter = new ReorderRuleFilter();

  return (
    <>
      <ExportReorderRuleModal open={exportModalOpen} setOpen={setExportModalOpen} groupIds={selected} />
      <DeleteReorderRuleModal open={deletedModalOpen} setOpen={setDeleteModalOpen} groupIds={[...selected]} />

      <div className={`${productMasterDataId ? '' : 'content-x'} flex h-full flex-col gap-4`}>
        <div className='flex justify-between gap-4'>
          <SearchBarWithFilter
            items={filteredRules}
            setItems={setItems}
            placeholder={t(TranslationKey.filterReorderRules).singular.label}
            filter={filter}
          />

          <div className='flex gap-2'>
            {selected.size ? (
              <>
                <ExportIconButton onClick={() => setExportModalOpen(true)} />
                <DeleteButton onClick={() => setDeleteModalOpen(true)} />
              </>
            ) : null}
            <AddButton onClick={() => navigate(Routing.productMasterData.settings.stockTriggers.create())} />
            <SettingsIconButton
              onClick={() =>
                navigate(Routing.productMasterData.settings.stockTriggers.settings.importConfigurations.overview())
              }
            />
          </div>
        </div>
        <Table<ReorderRuleGroup>
          loading={reorderRulesLoading || productMasterDataLoading}
          items={items}
          headers={getHeaders(productMasterData, stockLocations)}
          onClick={item => navigate(Routing.productMasterData.settings.stockTriggers.update(item.groupId))}
          onSelected={items => setSelected(new Set(items))}
          uniqueIdentifier='groupId'
          filter={filter}
        />
      </div>
    </>
  );
}
